import * as microsoftTeams from "@microsoft/teams-js";
import { ConfigurationApi } from "../apis/Configuration/ConfigurationApi";
import { getAccessTokenFromTeams } from "../services/AuthService/AuthService";
/** Query parameter carrying the Admin Center page a dialog relay frames. */
const ADMIN_CENTER_DIALOG_PARAMETER = "adminCenterDialog";
/**
 * The URL a Teams dialog opens to show an Admin Center page: this app's own page, which frames it.
 *
 * Admin Center's write endpoints (assign, checkout, create-user) need the user's Teams SSO token, and
 * Teams only issues one to a page on the domain of the app's webApplicationInfo resource. A dialog
 * showing Admin Center, or the Witivio subscriptions page that used to frame it, can never get one,
 * and Teams does not reliably carry messages between a dialog and the app that opened it. So the
 * dialog shows this app instead, which gets the token itself and hands it to the Admin Center it frames.
 */
export const getAdminCenterDialogUrl = (adminCenterUrl) => `${window.location.origin}/?${ADMIN_CENTER_DIALOG_PARAMETER}=${encodeURIComponent(adminCenterUrl)}`;
/**
 * Runs the relay instead of the app when this page was opened by {@link getAdminCenterDialogUrl}.
 * Called by each app's entry point before rendering: returns true when the app must not render.
 */
export const startAdminCenterDialogRelay = () => {
    const target = new URLSearchParams(window.location.search).get(ADMIN_CENTER_DIALOG_PARAMETER);
    if (target === null)
        return false;
    const teamsReady = microsoftTeams.app.initialize();
    runRelay(target, teamsReady)
        .catch((error) => showRelayError("Admin Center could not be opened", error))
        // The manifests set showLoadingIndicator: until notified, Teams keeps its spinner and then
        // replaces the dialog with "There was a problem reaching this app". Notified on failure too,
        // so the relay's own message is what the user sees.
        .finally(() => teamsReady.then(() => microsoftTeams.app.notifySuccess()).catch(() => undefined));
    return true;
};
const runRelay = async (target, teamsReady) => {
    const configuration = await ConfigurationApi.getConfiguration();
    // Only the configured Admin Center is framed, and only it is answered: the token is posted to its
    // origin, never to "*", and the target comes from the URL, which anyone can craft.
    const adminCenterOrigin = new URL(configuration.adminCenterBaseUrl).origin;
    const adminCenterUrl = new URL(target, adminCenterOrigin);
    if (adminCenterUrl.origin !== adminCenterOrigin)
        throw new Error(`Refusing to frame ${adminCenterUrl.origin}`);
    await teamsReady;
    const frame = document.createElement("iframe");
    frame.title = "Admin Center";
    frame.style.cssText = "position:fixed;inset:0;width:100%;height:100%;border:none";
    const postToAdminCenter = (message) => frame.contentWindow?.postMessage(message, adminCenterOrigin);
    window.addEventListener("message", (event) => {
        if (event.source !== frame.contentWindow || event.origin !== adminCenterOrigin)
            return;
        const data = event.data;
        if (typeof data === "object" && data !== null && "adminCenterCallback" in data) {
            microsoftTeams.dialog.url.submit(data.adminCenterCallback);
        }
        else if (data === "retrieveContextData") {
            microsoftTeams.app
                .getContext()
                .then((context) => postToAdminCenter({ teamsContext: context }))
                .catch((error) => console.error("Teams context unavailable for Admin Center", error));
        }
        else if (data === "retrieveSsoToken") {
            // Always answered, null when no token is available, so Admin Center stops waiting.
            getAccessTokenFromTeams()
                .then((result) => postToAdminCenter({ teamsSsoToken: result?.token ?? null }))
                .catch((error) => {
                console.error("Teams SSO token unavailable for Admin Center", error);
                postToAdminCenter({ teamsSsoToken: null });
            });
        }
    });
    frame.src = adminCenterUrl.href;
    document.body.style.margin = "0";
    document.body.appendChild(frame);
};
const showRelayError = (message, error) => {
    console.error(message, error);
    const paragraph = document.createElement("p");
    paragraph.textContent = message;
    paragraph.style.cssText = "font-family:Segoe UI,sans-serif;text-align:center;margin-top:40vh";
    document.body.appendChild(paragraph);
};
